import { ArrowLeftIcon } from "@heroicons/react/solid";
import {
  capitalize,
  get,
  isEmpty,
  map,
  pickBy,
  sumBy,
  uniq,
  values
} from "lodash";
import moment from "moment";
import Tooltip from "rc-tooltip";
import React, { useMemo } from "react";
import { Link, Route, RouteComponentProps, Switch } from "react-router-dom";
import CenteredLoading from "../../../../components/CenteredLoading";
import DonutChart, {
  DonutSection
} from "../../../../components/charts/DonutChart";
import HistogramChart from "../../../../components/charts/HistogramChart";
import Hex from "../../../../components/Hex";
import InfoIcon from "../../../../components/icons/InfoIcon";
import { getLabelColorStyle } from "../../../../components/labels/utils";
import NavLink from "../../../../components/NavLink";
import { useAppearance } from "../../../../context/appearance";
import { useFilteredList } from "../../../../context/filteredList";
import { useHotspots } from "../../../../context/hotspots";
import {
  LoadingKey,
  useLoading
} from "../../../../context/loading/LoadingProvider";
import { useLocations } from "../../../../context/locations";
import { useStats } from "../../../../context/stats";
import { renderPercentileClassification } from "../../../../context/stats/StatsProvider";
import classNames from "../../../../utils/classNames";
import { Entity } from "../../../../utils/constants";
import {
  alternativeRelativePerformanceTitles,
  classificationBoundaries,
  classificationColorMap,
  ClassificationValue,
  getClassification,
  relativePerformanceClassifications
} from "../../../../utils/hotspotUtils";
import useMapConfig from "../../../../utils/useMapConfig";
import VisibleHotspotsTab from "./VisibleHotspotsTab";
import VisibleLocationsTab from "./VisibleLocationsTab";

interface Props
  extends RouteComponentProps<{
    id: string;
    simulationUuid: string;
    subsection: string;
  }> {}

const SuperSimulationDetails: React.FC<Props> = ({ match, history }) => {
  const { getIsReady } = useLoading();
  const { stats, getPercentile } = useStats();
  const { hotspotsMap } = useHotspots();
  const { isDark } = useAppearance();
  const renderDistance = useMapConfig((state) => state.renderDistance);
  const { currentSuperSimulation } = useLocations();
  const { filteredList } = useFilteredList();

  const [visibleHotspots, visibleLocations] = useMemo(() => {
    const locations: string[] =
      currentSuperSimulation?.simulation_report?.vl || [];
    const hotspots: string[] =
      currentSuperSimulation?.simulation_report?.vh || [];
    return [hotspots, locations];
  }, [
    currentSuperSimulation?.simulation_report?.vl,
    currentSuperSimulation?.simulation_report?.vh
  ]);

  const visibleWitnessScoresMap = useMemo(
    () =>
      visibleHotspots.reduce(
        (acc, id) => {
          const hotspot = hotspotsMap[id];

          if (!hotspot) return acc;

          let classification:
            | "excellent"
            | "good"
            | "mediocre"
            | "poor"
            | "terrible"
            | "inactive";

          const witnessScore = hotspot.witness_score || 0;

          classification = getClassification(witnessScore);

          const { hotspots, witnessScores } = acc[classification];

          return {
            ...acc,
            [classification]: {
              hotspots: hotspots + 1,
              witnessScores: [...witnessScores, witnessScore]
            }
          };
        },
        {
          excellent: { hotspots: 0, witnessScores: [] },
          good: { hotspots: 0, witnessScores: [] },
          mediocre: { hotspots: 0, witnessScores: [] },
          poor: { hotspots: 0, witnessScores: [] },
          terrible: { hotspots: 0, witnessScores: [] },
          inactive: { hotspots: 0, witnessScores: [] }
        }
      ),
    [hotspotsMap, visibleHotspots]
  );

  const visibleWitnessScoresChartData: DonutSection[] = useMemo(
    () =>
      map(
        pickBy(visibleWitnessScoresMap, ({ hotspots }) => hotspots > 0),
        ({ hotspots }, key: string) => ({
          key,
          name: `${capitalize(key)}: ${uniq(classificationBoundaries[key]).join(
            "-"
          )}`,
          value: hotspots,
          displayValues: [`${hotspots} hotspots`],
          percentage:
            (hotspots * 100) /
            sumBy(values(visibleWitnessScoresMap), "hotspots"),
          color: get(classificationColorMap, key)
        })
      ),
    [visibleWitnessScoresMap]
  );

  const pocScore = useMemo(
    () => currentSuperSimulation?.simulation_report?.ps || 0,
    [currentSuperSimulation]
  );

  const { histogramDataMap, histogramBucketIndexMap } = useMemo(() => {
    const histogramDataMap: {
      [iteratee: string]: Array<{ name: string; count: number }>;
    } = {};

    const histogramBucketIndexMap: {
      [iteratee: string]: number;
    } = {};

    const filteredHotspots = filteredList[Entity.hotspot];

    if (isEmpty(filteredHotspots) || !stats) {
      return { histogramDataMap, histogramBucketIndexMap };
    }

    [
      {
        key: "witnesses",
        simulationValue: visibleHotspots.length,
        title: "number of valid witnessed others"
      },
      {
        key: "poc_score",
        simulationValue: pocScore,
        title: "PoC score"
      }
    ].forEach(({ key, simulationValue, title }) => {
      let renderBoundaryValue: ((value: number) => string) | undefined;

      switch (key) {
        case "witnesses":
          renderBoundaryValue = (value: number) =>
            value.toLocaleString(undefined);
          break;
        case "poc_score":
          renderBoundaryValue = (value: number) =>
            value.toLocaleString(undefined, {
              minimumFractionDigits: 2,
              maximumFractionDigits: 2
            });
          break;
      }

      histogramDataMap[key] = relativePerformanceClassifications.reduce(
        (
          acc: Array<{ name: string; count: number }>,
          classification: string,
          binIndex: number
        ) => {
          const isLastBucket =
            relativePerformanceClassifications.length - 1 === binIndex;

          if (binIndex === 0) {
            histogramBucketIndexMap[key] = binIndex;
          }

          const count = get(
            stats,
            ["histograms", key, classification, "count"],
            0
          );
          const bucketMaxValue = get(
            stats,
            ["histograms", key, classification, "boundary"],
            0
          );
          const bucketMinValue =
            binIndex === 0
              ? 0
              : get(
                  stats,
                  [
                    "histograms",
                    key,
                    relativePerformanceClassifications[binIndex - 1],
                    "boundary"
                  ],
                  0
                );

          if (
            simulationValue >= bucketMinValue &&
            (isLastBucket ? true : simulationValue < bucketMaxValue)
          ) {
            histogramBucketIndexMap[key] = binIndex;
          }

          let name = `hotspot${count === 1 ? "" : "s"} with ${
            alternativeRelativePerformanceTitles[classification]
          } ${title}`;

          if (renderBoundaryValue) {
            name += ` of ${renderBoundaryValue(
              bucketMinValue
            )} to ${renderBoundaryValue(bucketMaxValue)}`;
          }

          return [...acc, { name, count }];
        },
        []
      );
    });

    return { histogramDataMap, histogramBucketIndexMap };
  }, [filteredList, pocScore, stats, visibleHotspots.length]);

  const tabs = useMemo(
    () => [
      {
        key: "visible-hotspots",
        title: "Visible hotspots",
        count: visibleHotspots.length
      },
      {
        key: "visible-locations",
        title: "Visible locations",
        count: visibleLocations.length
      }
    ],
    [visibleHotspots.length, visibleLocations.length]
  );

  const isLoadingHotspots = useMemo(
    () =>
      !getIsReady([LoadingKey.hotspotsAroundLocations]) || isEmpty(hotspotsMap),
    [getIsReady, hotspotsMap]
  );

  const pocScorePercentile = useMemo(
    () => (pocScore ? getPercentile("poc_score", pocScore) : undefined),
    [getPercentile, pocScore]
  );

  if (!currentSuperSimulation || isLoadingHotspots) {
    return <CenteredLoading className="h-full" />;
  }

  if (!currentSuperSimulation.simulation_report) {
    return <div>Pending simulation results</div>; // TODO: keep fetching the details
  }

  return (
    <div className="mt-6">
      <Link
        className="text-sm flex items-center hover:text-gray-600 truncate font-semibold dark:hover:text-white dark:hover:underline"
        to={`/locations/${currentSuperSimulation.location_uuid}/super-simulations`}
      >
        <ArrowLeftIcon className="h-3 w-3 inline mr-1" />
        Return
      </Link>

      <h1 className="text-3xl font-extrabold text-gray-900 tracking-tight sm:text-4xl dark:text-gray-50">
        Super Simulation
      </h1>
      <div className="flex items-center flex-wrap">
        <div
          className="inline-flex items-center rounded-full font-medium px-3 text-sm mr-2 mt-2"
          style={getLabelColorStyle("#7057ff", isDark)}
        >
          Height: {renderDistance(currentSuperSimulation.height, false)}
        </div>
        <div
          className="inline-flex items-center rounded-full font-medium px-3 text-sm mt-2"
          style={getLabelColorStyle("#d73aaa", isDark)}
        >
          Created {moment(currentSuperSimulation.created_at).fromNow()}
        </div>
      </div>

      <div className="mt-6">
        <dl className="mt-5 grid grid-cols-1 gap-5 sm:grid-cols-3">
          <div>
            <div className="relative bg-white dark:bg-gray-800 overflow-hidden shadow rounded-lg">
              <div className="px-4 py-5 sm:p-6">
                <dt className="text-sm font-medium text-gray-500 dark:text-gray-300 truncate flex items-center">
                  Line of sight visibility
                  <Tooltip
                    placement="top"
                    overlay={`The number of hotspots that are visible from this location at an installation height of ${renderDistance(
                      currentSuperSimulation?.simulation_report?.h,
                      false
                    )} is compared against how many witnesses hotspots have in the entire network.`}
                  >
                    <InfoIcon className="inline-flex ml-1 h-4 w-4" />
                  </Tooltip>
                </dt>

                <dd className="mt-1 text-3xl font-semibold text-gray-900 dark:text-white">
                  <div className="flex items-baseline">
                    <div className="mt-1 text-3xl font-semibold text-gray-900 dark:text-white">
                      <div className="flex items-center">
                        <span className="flex items-center justify-center">
                          <Hex
                            fillColor={
                              classificationColorMap[
                                relativePerformanceClassifications[
                                  get(histogramBucketIndexMap, "witnesses", 0)
                                ]
                              ]
                            }
                            width={20}
                            height={22.8}
                          />
                        </span>

                        <p className="ml-2">
                          {visibleHotspots.length.toLocaleString(undefined)}
                        </p>
                      </div>
                    </div>
                    <div className="ml-2 flex items-baseline text-sm">
                      visible hotspot{visibleHotspots.length === 1 ? "" : "s"}
                    </div>
                  </div>

                  <div className="w-full h-[212px]">
                    {isEmpty(histogramDataMap["witnesses"]) ? (
                      <CenteredLoading className="h-full" />
                    ) : (
                      <HistogramChart
                        data={histogramDataMap["witnesses"]}
                        activeBucketIndex={get(
                          histogramBucketIndexMap,
                          "witnesses",
                          0
                        )}
                      />
                    )}
                  </div>
                </dd>
              </div>
            </div>
          </div>

          <div>
            <div className="bg-white dark:bg-gray-800 overflow-hidden shadow rounded-lg">
              <div className="px-4 py-5 sm:p-6">
                <dt className="text-sm font-medium text-gray-500 dark:text-gray-300 truncate flex items-center">
                  Visible hotspot Witness Scores
                  <Tooltip
                    placement="top"
                    overlay="The Witness Score is a number that represents the PoC rewards that hotspots can earn for witnessing this hotspot. It is calculated as the product of the transmit scale and witness reward units of this hotspot."
                  >
                    <InfoIcon className="inline-flex ml-1 h-4 w-4" />
                  </Tooltip>
                </dt>

                <dd className="mt-4">
                  <DonutChart
                    className="mx-auto"
                    data={visibleWitnessScoresChartData}
                    sizeInPx={240}
                  />
                </dd>
              </div>
            </div>
          </div>

          <div>
            <div className="relative bg-white dark:bg-gray-800 overflow-hidden shadow rounded-lg">
              <div className="px-4 py-5 sm:p-6">
                <dt className="text-sm font-medium text-gray-500 dark:text-gray-300 truncate flex items-center">
                  PoC Score
                  <Tooltip
                    placement="top"
                    overlay="The PoC Score is a number that represents the PoC rewards potential in a way that can be directly compared to all other hotspots in the Helium network."
                  >
                    <InfoIcon className="inline-flex ml-1 h-4 w-4" />
                  </Tooltip>
                </dt>

                <dd className="mt-1 text-3xl font-semibold text-gray-900 dark:text-white">
                  <div className="flex items-baseline">
                    <div className="mt-1 text-3xl font-semibold text-gray-900 dark:text-white">
                      {pocScorePercentile != null ? (
                        <ClassificationValue
                          value={pocScorePercentile / 100}
                          alternativeContent={capitalize(
                            renderPercentileClassification(pocScorePercentile)
                          )}
                          withoutTransmitScaleTooltip
                          hexProps={{
                            width: 20,
                            height: 22.8
                          }}
                        />
                      ) : (
                        "-"
                      )}
                    </div>
                    <div className="ml-2 text-xs">
                      Value:{" "}
                      {pocScore.toLocaleString(undefined, {
                        minimumFractionDigits: 2,
                        maximumFractionDigits: 2
                      })}
                    </div>
                  </div>

                  <div className="w-full h-[212px]">
                    {isEmpty(histogramDataMap["poc_score"]) ? (
                      <CenteredLoading className="h-full" />
                    ) : (
                      <HistogramChart
                        data={histogramDataMap["poc_score"]}
                        activeBucketIndex={get(
                          histogramBucketIndexMap,
                          "poc_score",
                          0
                        )}
                      />
                    )}
                  </div>
                </dd>
              </div>
            </div>
          </div>
        </dl>
      </div>

      <div className="my-6">
        <select
          className="sm:hidden block w-full pl-3 py-2 sm:text-sm rounded-md dark:bg-gray-800"
          onChange={(e) => {
            history.push(
              `/locations/${match.params.id}/super-simulations/${match.params.simulationUuid}/${e.target.value}`
            );
          }}
          value={match.params.subsection}
        >
          {tabs.map(({ key, title, count }) => (
            <option key={key} value={key}>
              {title}
              {count ? ` (${count.toLocaleString(undefined)})` : null}
            </option>
          ))}
        </select>
        <div className="hidden sm:block">
          <nav className="flex space-x-4" aria-label="Tabs">
            {tabs.map(({ key, title, count }) => (
              <NavLink
                key={key}
                to={`/locations/${match.params.id}/super-simulations/${match.params.simulationUuid}/${key}`}
                activeClassName="bg-purple-100 text-purple-700 dark:text-white dark:bg-gray-700 dark:text-white"
                inactiveClassName="text-gray-500 hover:text-gray-700 hover:bg-gray-50 group dark:text-gray-400 dark:hover:text-gray-300 dark:hover:bg-transparent"
                className="px-3 py-2 font-medium text-sm rounded-md"
              >
                {title}
                {count ? (
                  <span
                    className={classNames(
                      match.params.subsection === key
                        ? "bg-purple-500 text-gray-100 dark:bg-gray-500"
                        : "bg-gray-200 text-gray-500 group-hover:text-gray-700 dark:text-gray-200 dark:bg-gray-600 dark:group-hover:text-gray-200 dark:group-hover:bg-gray-500",
                      "hidden ml-3 py-0.5 px-2.5 rounded-full text-xs font-medium md:inline-block"
                    )}
                  >
                    {count.toLocaleString(undefined)}
                  </span>
                ) : null}
              </NavLink>
            ))}
          </nav>
        </div>
      </div>
      <Switch>
        <Route
          path="/locations/:locationUuid/super-simulations/:simulationUuid/visible-hotspots"
          component={VisibleHotspotsTab}
        />
        <Route
          path="/locations/:locationUuid/super-simulations/:simulationUuid/visible-locations"
          component={VisibleLocationsTab}
        />
      </Switch>
    </div>
  );
};

export default SuperSimulationDetails;
